/**
 * One Credit Note or Sales Return: preview, print, issue.
 *
 * THE TWO TYPES DIVERGE EXACTLY ONCE HERE
 * --------------------------------------
 * Everything else on this screen is shared. The single difference is what the
 * issue button PROMISES, and it must be spelled out before the user commits:
 *
 *   CREDIT NOTE  — credits the customer back. No goods moved, so no stock moves.
 *   SALES RETURN — credits the customer back AND books the goods into stock.
 *
 * Saying "no stock" and doing otherwise is the worst version of this screen: the
 * user would have released goods into their inventory without knowing, and the
 * only trace would be a ledger row they had no reason to look for.
 */
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { FileDown, Printer, Send } from 'lucide-react';
import { A4_HEIGHT_PX, A4_WIDTH_PX, fitScale, renderPreview } from '@core/doc/preview';
import { printCapabilities, printDocument, saveDocumentPdf } from '@data/services/printService';
import {
  returnTypeLabel,
  returnsGoods,
  type ReturnDocType,
} from '@data/services/salesReturnService';
import { toast } from '@ui/components/Toaster';
import { useIssueReturn, useReturnDocument } from './useSalesReturns';

const assets = new URL('assets/', document.baseURI).href;

const BASE: Record<ReturnDocType, string> = {
  CREDIT_NOTE: '#/sales/credit-notes',
  SALES_RETURN: '#/sales/returns',
};

export function ReturnDocumentScreen({
  documentId,
  docType,
}: {
  documentId: string;
  docType: ReturnDocType;
}) {
  const query = useReturnDocument(documentId);
  const issueMutation = useIssueReturn();
  const capabilities = printCapabilities();
  const [busy, setBusy] = useState(false);
  const scale = fitScale(window.innerWidth * 0.64);
  const label = returnTypeLabel(docType);

  if (query.isLoading) {
    return <p className="p-4 text-sm text-slate-400">Loading {label.toLowerCase()}…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <p role="alert" className="p-4 text-sm text-red-300">
        Could not open this {label.toLowerCase()}.
      </p>
    );
  }
  const { model, stored, business, invoice, renderOptions } = query.data;
  const html = renderPreview(model, business, { scale, ...renderOptions }, assets);
  const physical = returnsGoods(docType);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  async function print() {
    setBusy(true);
    try {
      await printDocument(model, business, assets);
    } catch (error) {
      toast({
        title: `Could not print the ${label.toLowerCase()}`,
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      flushSync(() => setBusy(false));
    }
  }

  async function savePdf() {
    await run(async () => {
      try {
        const result = await saveDocumentPdf(model, business, assets);
        if (result.saved) {
          toast({ title: 'PDF saved', description: result.filePath, tone: 'success' });
        }
      } catch (error) {
        toast({
          title: `Could not save the ${label.toLowerCase()} PDF`,
          description: error instanceof Error ? error.message : 'Try again.',
          tone: 'danger',
        });
      }
    });
  }

  async function issue() {
    const effect = physical
      ? 'The customer is credited and the returned goods are booked back into stock.'
      : 'The customer is credited. No goods moved, so stock is untouched.';
    if (
      !window.confirm(
        `Issue ${label} ${stored.docNumber}?\n\n${effect}\n\nIt cannot be edited afterwards.`,
      )
    ) {
      return;
    }
    await run(async () => {
      try {
        await issueMutation.mutateAsync(documentId);
        await query.refetch();
        toast({
          title: `${label} ${stored.docNumber} issued`,
          description: physical
            ? 'The customer was credited and the goods were booked back into stock.'
            : 'The customer was credited. Stock was not touched.',
          tone: 'success',
        });
      } catch (error) {
        toast({
          title: `Could not issue the ${label.toLowerCase()}`,
          description: error instanceof Error ? error.message : 'Try again.',
          tone: 'danger',
        });
      }
    });
  }

  return (
    <div className="flex flex-col items-center gap-3 p-4 text-slate-100">
      <header className="flex w-full max-w-4xl flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-semibold">
            {label} {stored.docNumber}
          </h1>
          <p data-testid="return-status" className="text-xs text-slate-400">
            {stored.status === 'DRAFT'
              ? physical
                ? 'Draft · nothing credited and no stock moved'
                : 'Draft · nothing credited and stock untouched'
              : stored.status === 'ISSUED'
                ? physical
                  ? 'Issued · customer credited and goods booked back into stock'
                  : 'Issued · customer credited, stock untouched'
                : `Cancelled · ${
                    physical
                      ? 'the booked-in goods have been taken back out'
                      : 'the credit reversed'
                  }`}
          </p>
          {invoice && (
            <p data-testid="return-source" className="text-xs text-slate-500">
              Against invoice {invoice.docNumber} · {invoice.docDate}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {stored.status === 'DRAFT' && (
            <>
              <a
                className="rounded border border-slate-700 px-3 py-2 text-sm"
                href={`${BASE[docType]}/${documentId}/edit`}
              >
                Edit draft
              </a>
              <button
                data-testid="issue-return"
                disabled={busy}
                onClick={() => void issue()}
                className="rounded bg-emerald-700 px-3 py-2 text-sm"
              >
                <Send className="mr-1 inline h-4 w-4" />
                Issue {label}
              </button>
            </>
          )}
          <button
            data-testid="print-return"
            disabled={busy}
            onClick={() => void print()}
            className="rounded bg-brand-500 px-3 py-2 text-sm"
          >
            <Printer className="mr-1 inline h-4 w-4" />
            Print / Reprint
          </button>
          {capabilities.silentPdf && (
            <button
              data-testid="save-pdf-return"
              disabled={busy}
              onClick={() => void savePdf()}
              className="rounded border border-slate-700 px-3 py-2 text-sm"
            >
              <FileDown className="mr-1 inline h-4 w-4" />
              Save PDF
            </button>
          )}
        </div>
      </header>

      <iframe
        title={`${label} preview`}
        data-testid="return-preview"
        srcDoc={html}
        style={{ width: A4_WIDTH_PX * scale, height: A4_HEIGHT_PX * scale, border: 0 }}
      />
      <p className="text-xs text-slate-500">
        Preview · the original invoice is never edited ·{' '}
        {physical ? 'issuing books the goods back into stock' : 'issuing never moves stock'}
      </p>
      <a className="text-sm text-brand-400 underline" href={BASE[docType]}>
        Back to {label.toLowerCase()}s
      </a>
    </div>
  );
}